import { type FC, type MouseEvent } from 'react';
import { FormattedMessage, FormattedNumber, useIntl, defineMessages } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import Close from 'dibs-icons/exports/Close.ts';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { getSrcsetUrl } from 'dibs-image-utils/exports/srcSet.ts';

import { trackCartAddedGoToCart } from './trackCartAddedGoToCart.ts';
import cartNavStyles from '../CartNav/CartNav-style.scss';

type CartAddedNotificationContentProps = {
    title: string | null;
    imagePath: string | null;
    price: number | null;
    currency: string;
    onClose: () => void;
    isMobile: boolean;
};

const messages = defineMessages({
    close: { id: 'dbl.CartAddedNotification.close', defaultMessage: 'Close' },
});

export const CartAddedNotificationContent: FC<CartAddedNotificationContentProps> = ({
    title,
    imagePath,
    price,
    currency,
    onClose,
    isMobile,
}) => {
    const intl = useIntl();
    const cartUrl = handleLocaleUrl('/my/cart/', intl.locale);

    const handleGoToCart = (event: MouseEvent<HTMLElement>): void => {
        trackCartAddedGoToCart(event, isMobile);
    };

    let itemPriceNode = null;
    if (price) {
        itemPriceNode = <FormattedNumber style="currency" currency={currency} value={price} />;
    }

    return (
        <div data-tn="cart-added-notification">
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.justifyBetween,
                    dibsCss.pSmall,
                    dibsCss.borderB,
                    dibsCss.borderBSolid,
                    dibsCss.borderBDolphin
                )}
            >
                <div
                    className={classnames(
                        dibsCss.sassyFontSubHeaderTypeHeavy,
                        dibsCss.textDolphin,
                        dibsCss.textPitchblack
                    )}
                >
                    <FormattedMessage
                        id="dbl.CartAddedNotification.title"
                        defaultMessage="Added to Cart"
                    />
                </div>
                <Link
                    onClick={onClose}
                    ariaLabel={intl.formatMessage(messages.close)}
                    dataTn="cart-added-notification-close"
                    className={classnames(dibsCss.fillNoir, dibsCss.flex, dibsCss.itemsCenter)}
                >
                    <Close variation="24" />
                </Link>
            </div>
            <div
                className={classnames(
                    cartNavStyles.dropdownItemRow,
                    dibsCss.sassyFontBody,
                    dibsCss.flex,
                    dibsCss.wFull,
                    dibsCss.textLeft,
                    dibsCss.textPitchblack
                )}
            >
                <div className={classnames(dibsCss.py0, dibsCss.pxSmall)}>
                    <div
                        className={classnames(
                            cartNavStyles.dropdownItemImageWrapper,
                            dibsCss.relative
                        )}
                    >
                        {imagePath ? (
                            <img
                                className={classnames(
                                    cartNavStyles.dropdownItemImage,
                                    dibsCss.mAuto,
                                    dibsCss.absolute,
                                    dibsCss.inset0
                                )}
                                src={getSrcsetUrl(imagePath, 75)}
                                alt={title || ''}
                            />
                        ) : null}
                    </div>
                </div>
                <div className={classnames(dibsCss.py0, dibsCss.plXsmall, dibsCss.prSmall)}>
                    <div className={cartNavStyles.dropdownItemTitle}>{title}</div>
                    <div data-tn="cart-added-notification-item-price">{itemPriceNode}</div>
                </div>
            </div>
            <div className={dibsCss.pSmall}>
                <Button
                    type="primary"
                    size="large"
                    fullWidth
                    href={cartUrl}
                    onClick={handleGoToCart}
                    dataTn="cart-added-notification-go-to-cart"
                >
                    <FormattedMessage
                        id="dbl.CartAddedNotification.goToCart"
                        defaultMessage="Go To Cart"
                    />
                </Button>
            </div>
        </div>
    );
};
